import React, { useCallback, useEffect, useState } from "react";
import {
    createPromAttributeMapping,
    createPromAttributeValueMapping,
    approveSuggestedPromAttributeMappings,
    getPromAttributeFeedFields,
    getPromAttributeMappingSummary,
    getPromAttributeMappings,
    getPromAttributeSchema,
    getPromAttributeValueMappings,
    suggestPromAttributeMappings,
    updatePromAttributeMapping,
    updatePromAttributeValueMapping,
    uploadPromAttributeSchema,
} from "../api/promApi";
import "../pages/IntegrationsPage.css";

const PromAttributeMappingPanel = ({ accountId, categoryId = "23005", categoryName = "Біодобавки", onStatus }) => {
    const [file, setFile] = useState(null);
    const [schema, setSchema] = useState([]);
    const [fields, setFields] = useState([]);
    const [mappings, setMappings] = useState([]);
    const [summary, setSummary] = useState(null);
    const [expanded, setExpanded] = useState({});
    const [values, setValues] = useState({});
    const [manual, setManual] = useState({ definitionId: "", promAttributeId: "" });
    const [valueDrafts, setValueDrafts] = useState({});

    const load = useCallback(async () => {
        if (!accountId) return;
        const [schemaRows, fieldRows, mappingRows, summaryRow] = await Promise.all([
            getPromAttributeSchema(accountId, categoryId), getPromAttributeFeedFields(accountId, categoryId),
            getPromAttributeMappings(accountId, categoryId), getPromAttributeMappingSummary(accountId, categoryId),
        ]);
        setSchema(schemaRows || []); setFields(fieldRows || []); setMappings(mappingRows || []); setSummary(summaryRow);
    }, [accountId, categoryId]);
    useEffect(() => { load().catch((error) => onStatus(error?.response?.data?.detail || "Не удалось загрузить mapping характеристик")); }, [load, onStatus]);

    const runSuggestions = async () => {
        const result = await suggestPromAttributeMappings(accountId, categoryId);
        onStatus(`Создано предложений: свойств ${result.attribute_suggestions}, значений ${result.value_suggestions}.`);
        await load();
    };
    const toggleValues = async (mapping) => {
        if (!expanded[mapping.id]) setValues({ ...values, [mapping.id]: await getPromAttributeValueMappings(accountId, categoryId, mapping.id) });
        setExpanded({ ...expanded, [mapping.id]: !expanded[mapping.id] });
    };
    const updateMapping = async (mapping, status) => { await updatePromAttributeMapping(accountId, categoryId, mapping.id, { status }); await load(); };
    const updateValue = async (mapping, value, status) => {
        await updatePromAttributeValueMapping(accountId, categoryId, mapping.id, value.id, { status });
        setValues({ ...values, [mapping.id]: await getPromAttributeValueMappings(accountId, categoryId, mapping.id) });
    };
    const createManualValue = async (mapping) => {
        const draft = valueDrafts[mapping.id] || {};
        await createPromAttributeValueMapping(accountId, categoryId, mapping.id, { source_value: draft.sourceValue, prom_value_id: Number(draft.promValueId), status: "approved" });
        setValues({ ...values, [mapping.id]: await getPromAttributeValueMappings(accountId, categoryId, mapping.id) });
        setValueDrafts({ ...valueDrafts, [mapping.id]: { sourceValue: "", promValueId: "" } });
    };
    const createManual = async () => {
        await createPromAttributeMapping(accountId, categoryId, { definition_id: Number(manual.definitionId), prom_attribute_id: Number(manual.promAttributeId), status: "approved" });
        setManual({ definitionId: "", promAttributeId: "" }); await load();
    };
    const approveAll = async (kind) => {
        const result = await approveSuggestedPromAttributeMappings(accountId, categoryId, kind);
        onStatus(`Подтверждено: ${result.approved}.`); await load();
    };
    const mappedDefinitionIds = new Set(mappings.map((row) => row.definition.id));

    return <section className="ip-card">
        <div className="ip-card-body">
            <div>
                <h2 className="ip-title">Характеристики Prom: {categoryId} — {categoryName}</h2>
                <p className="ip-lead">Схема и mappings принадлежат выбранному кабинету. Карточки и Prom feed пока не меняются.</p>
            </div>
            <div className="ip-acts" style={{ marginLeft: 0, flexWrap: "wrap" }}>
                <input type="file" className="ip-inp" style={{ width: "auto" }} accept=".xml,.md,text/xml,text/markdown" onChange={(event) => setFile(event.target.files?.[0] || null)} />
                <button type="button" className="ip-btn ip-btn-primary" disabled={!file} onClick={() => uploadPromAttributeSchema(accountId, categoryId, file).then((r) => { onStatus(`Схема импортирована: ${r.imported_attributes} свойств, ${r.imported_values} значений.`); return load(); }).catch((e) => onStatus(e?.response?.data?.detail || "Не удалось импортировать файл"))}>Загрузить XML / MD</button>
                <button type="button" className="ip-btn" onClick={() => runSuggestions().catch((e) => onStatus(e?.response?.data?.detail || "Не удалось создать предложения"))}>Предложить сопоставления</button>
                <button type="button" className="ip-btn" onClick={() => approveAll("attributes").catch((e) => onStatus(e?.response?.data?.detail || "Не удалось подтвердить свойства"))}>Подтвердить все свойства</button>
                <button type="button" className="ip-btn" onClick={() => approveAll("values").catch((e) => onStatus(e?.response?.data?.detail || "Не удалось подтвердить значения"))}>Подтвердить все значения</button>
                <button type="button" className="ip-btn" onClick={() => load().catch((e) => onStatus(e?.response?.data?.detail || "Не удалось обновить данные"))}>Обновить</button>
            </div>
            {summary && (
                <div className="ip-callout muted" style={{ marginBottom: 0 }}>
                    <span className="ip-ic">ℹ</span>
                    <span>Prom: {summary.imported_attributes} свойств / {summary.imported_values} значений · Mapping: {summary.approved_attributes}/{summary.mapped_attributes} свойств, {summary.approved_values}/{summary.mapped_values} значений подтверждено</span>
                </div>
            )}
            <div className="ip-accordion">
                <details>
                    <summary className="ip-ac-head" style={{ cursor: "pointer" }}>
                        <span className="ip-nm">Схема Prom ({schema.length})</span>
                        <span className="ip-chev">⌄</span>
                    </summary>
                    <div className="ip-ac-body">
                        <div className="ip-tbl-wrap">
                            <table className="ip-tbl">
                                <thead><tr><th>ID</th><th>Свойство UA / RU</th><th>Тип</th><th>Ед.</th><th>Значения</th></tr></thead>
                                <tbody>{schema.map((row) => <tr key={row.id}><td>{row.prom_attribute_id}</td><td>{row.name_ua}<br /><small className="ip-help-text">{row.name_ru}</small></td><td>{row.value_type}</td><td>{row.unit_ua || "—"}</td><td>{row.values?.length || 0}</td></tr>)}</tbody>
                            </table>
                        </div>
                    </div>
                </details>
            </div>
            <div className="ip-block">
                <div className="ip-bh"><span className="ip-ic">🔗</span><h4>Связать вручную</h4></div>
                <div className="ip-grid3" style={{ alignItems: "end" }}>
                    <label className="ip-fld"><span>Свойство master catalog</span>
                        <select className="ip-inp" value={manual.definitionId} onChange={(e) => setManual({ ...manual, definitionId: e.target.value })}>
                            <option value="">Свойство master catalog</option>
                            {fields.filter((row) => row.definition && !mappedDefinitionIds.has(row.definition.id)).map((row) => <option key={row.definition.id} value={row.definition.id}>{row.definition.name_ua}</option>)}
                        </select>
                    </label>
                    <label className="ip-fld"><span>Свойство Prom</span>
                        <select className="ip-inp" value={manual.promAttributeId} onChange={(e) => setManual({ ...manual, promAttributeId: e.target.value })}>
                            <option value="">Свойство Prom</option>
                            {schema.filter((row) => row.is_active).map((row) => <option key={row.id} value={row.id}>{row.name_ua} ({row.prom_attribute_id})</option>)}
                        </select>
                    </label>
                    <button type="button" className="ip-btn ip-btn-primary" disabled={!manual.definitionId || !manual.promAttributeId} onClick={() => createManual().catch((e) => onStatus(e?.response?.data?.detail || "Не удалось создать mapping"))}>Связать вручную</button>
                </div>
            </div>
            <div className="ip-block">
                <div className="ip-bh"><span className="ip-ic">🔀</span><h4>Сопоставления свойств</h4></div>
                <div className="ip-tbl-wrap">
                    <table className="ip-tbl">
                        <thead><tr><th>Master catalog</th><th>Prom</th><th>Метод</th><th>Статус</th><th>Действия</th></tr></thead>
                        <tbody>{mappings.map((row) => <React.Fragment key={row.id}>
                            <tr>
                                <td>{row.definition.name_ua}</td>
                                <td>{row.prom_attribute.name_ua}</td>
                                <td>{row.match_method || "—"} {row.confidence != null ? `(${Math.round(row.confidence * 100)}%)` : ""}</td>
                                <td><span className={`ip-pill ${row.status === "approved" ? "g" : row.status === "ignored" ? "off" : "warn"}`}>{row.status}</span></td>
                                <td style={{ whiteSpace: "nowrap" }}>
                                    <button type="button" className="ip-btn ip-btn-sm" onClick={() => updateMapping(row, "approved")}>Подтвердить</button>{" "}
                                    <button type="button" className="ip-btn ip-btn-sm" onClick={() => updateMapping(row, "ignored")}>Игнорировать</button>{" "}
                                    {["singleselect", "multiselect"].includes(row.prom_attribute.value_type) && <button type="button" className="ip-btn ip-btn-sm" onClick={() => toggleValues(row)}>{expanded[row.id] ? "Скрыть значения" : "Значения"}</button>}
                                </td>
                            </tr>
                            {expanded[row.id] && (
                                <tr>
                                    <td colSpan="5">
                                        <div style={{ display: "grid", gap: "6px", marginBottom: "10px" }}>
                                            {(values[row.id] || []).map((value) => (
                                                <div key={value.id} style={{ display: "flex", gap: "10px", alignItems: "center", flexWrap: "wrap", fontSize: "13px" }}>
                                                    <span>{value.source_value} → {value.prom_value.name_ua || value.prom_value.name_ru}</span>
                                                    <span className={`ip-pill ${value.status === "approved" ? "g" : value.status === "ignored" ? "off" : "warn"}`}>{value.status}</span>
                                                    <button type="button" className="ip-btn ip-btn-sm" onClick={() => updateValue(row, value, "approved")}>Подтвердить</button>
                                                    <button type="button" className="ip-btn ip-btn-sm" onClick={() => updateValue(row, value, "ignored")}>Игнорировать</button>
                                                </div>
                                            ))}
                                        </div>
                                        <div className="ip-grid3">
                                            <label className="ip-fld"><span>Значение master catalog</span>
                                                <input className="ip-inp" placeholder="Значение master catalog" value={valueDrafts[row.id]?.sourceValue || ""} onChange={(e) => setValueDrafts({ ...valueDrafts, [row.id]: { ...(valueDrafts[row.id] || {}), sourceValue: e.target.value } })} />
                                            </label>
                                            <label className="ip-fld"><span>Значение Prom</span>
                                                <select className="ip-inp" value={valueDrafts[row.id]?.promValueId || ""} onChange={(e) => setValueDrafts({ ...valueDrafts, [row.id]: { ...(valueDrafts[row.id] || {}), promValueId: e.target.value } })}>
                                                    <option value="">Значение Prom</option>
                                                    {row.prom_attribute.values.map((item) => <option key={item.id} value={item.id}>{item.name_ua || item.name_ru}</option>)}
                                                </select>
                                            </label>
                                            <button type="button" className="ip-btn ip-btn-primary" disabled={!valueDrafts[row.id]?.sourceValue || !valueDrafts[row.id]?.promValueId} onClick={() => createManualValue(row).catch((e) => onStatus(e?.response?.data?.detail || "Не удалось связать значение"))}>Связать вручную</button>
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </React.Fragment>)}</tbody>
                    </table>
                </div>
            </div>
        </div>
    </section>;
};

export default PromAttributeMappingPanel;
